import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Smile, ThumbsUp, AlertCircle, Info } from 'lucide-react';
import ProgressRing from '../components/ui-kit/ProgressRing';
import { getSentimentHistory } from '../lib/api';

const TONE_LABELS = { confident: 'Confident', neutral: 'Neutral', nervous: 'Nervous', hesitant: 'Hesitant' };
const TONE_COLORS = {
  confident: 'linear-gradient(90deg, #22C55E 0%, #16A34A 100%)',
  neutral: 'linear-gradient(90deg, #9BA1B0 0%, #6B6480 100%)',
  nervous: 'linear-gradient(90deg, #FF6B1A 0%, #EA580C 100%)',
  hesitant: 'linear-gradient(90deg, #FFC233 0%, #C68F00 100%)',
};

export default function SentimentPage() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getSentimentHistory();
        if (!cancelled) setSessions(data.sessions || []);
      } catch (e) {
        if (!cancelled) setError('Could not load sentiment analysis.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[10px] font-heading uppercase tracking-[0.2em] text-[#3D1F6E] dark:text-[#A78BFA] font-semibold mb-1">
          Analytics
        </p>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-heading">
          Sentiment Analysis
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Emotional tone and confidence patterns across your interviews
        </p>
      </div>

      <div className="flex items-start gap-2.5 rounded-lg border border-[#3D1F6E]/20 bg-[#3D1F6E]/5 dark:border-[#A78BFA]/20 dark:bg-[#A78BFA]/5 p-3">
        <Info className="w-4 h-4 text-[#3D1F6E] dark:text-[#A78BFA] shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground">
          This is a preview build — this view will move to trainer/college-admin dashboards once fully integrated.
        </p>
      </div>

      {error && <p className="text-sm text-red-500" data-testid="sentiment-error">{error}</p>}

      {!loading && sessions.length === 0 ? (
        <Card className="bg-card border-border card-shadow">
          <CardContent className="flex flex-col items-center py-10 gap-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center icon-bg-violet">
              <Smile className="w-7 h-7" />
            </div>
            <div className="text-center space-y-1">
              <h3 className="text-base font-heading font-semibold">No completed interviews yet</h3>
              <p className="text-sm text-muted-foreground max-w-sm">
                Complete a mock interview to see your sentiment analysis and confidence score here.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="sentiment-session-list">
          {sessions.map((s) => (
            <SentimentSessionCard key={s.session_id} session={s} />
          ))}
        </div>
      )}
    </div>
  );
}

function SentimentSessionCard({ session }) {
  const label = session.mode === 'company' ? session.company : session.topic;
  const date = session.ended_at
    ? new Date(session.ended_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';
  const toneEntries = Object.entries(session.tone_breakdown || {});

  return (
    <Card className="bg-card border-border card-shadow" data-testid={`sentiment-card-${session.session_id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-sm font-heading font-semibold text-[#3D1F6E] dark:text-[#A78BFA]">
              {label || 'Interview'}
            </CardTitle>
            <p className="text-[11px] text-muted-foreground mt-0.5">{date}</p>
          </div>
          <Badge variant="secondary" className="text-[11px] font-heading">{session.overall_sentiment}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col items-center gap-2">
            <ProgressRing value={session.sentiment_score || 0} size={84} strokeWidth={7} label="Sentiment" />
          </div>
          <div className="flex flex-col items-center gap-2">
            <ProgressRing value={session.confidence_score || 0} size={84} strokeWidth={7} label="Confidence" />
          </div>
        </div>

        {toneEntries.length > 0 && (
          <div className="space-y-2.5">
            <p className="text-[11px] font-heading uppercase tracking-wide text-muted-foreground">Tone Breakdown</p>
            {toneEntries.map(([tone, pct]) => (
              <div key={tone} className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-medium">{TONE_LABELS[tone] || tone}</span>
                  <span className="text-[12px] font-heading font-bold tabular-nums">{Math.round(pct)}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${Math.max(pct, 2)}%`, background: TONE_COLORS[tone] || TONE_COLORS.neutral }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {session.key_moments?.length > 0 && (
          <div className="space-y-2">
            <p className="text-[11px] font-heading uppercase tracking-wide text-muted-foreground">Key Moments</p>
            <ul className="space-y-2">
              {session.key_moments.map((m, i) => (
                <li key={i} className="flex gap-2.5 text-[13px] leading-relaxed">
                  {m.type === 'strength' ? (
                    <ThumbsUp className="w-3.5 h-3.5 text-[#22C55E] shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-3.5 h-3.5 text-[#FF6B1A] shrink-0 mt-0.5" />
                  )}
                  <span>{m.note}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {session.summary && (
          <p className="text-[13px] leading-relaxed text-foreground/90 border-t border-border pt-3">
            {session.summary}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
